import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SaveButton } from "@/components/properties/SaveButton";
import {
  Bed,
  Bath,
  Square,
  MapPin,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
} from "lucide-react";

const propertyTypeLabels = {
  "short-term": "Vacation Rental",
  "commercial": "Commercial",
  "long-term": "Long-Term Rental",
};

export interface Property {
  id: string;
  title: string;
  description: string | null;
  property_type: string;
  price: number | null;
  price_period: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  square_feet: number | null;
  city: string | null;
  state: string | null;
  address?: string | null;
  zip_code?: string | null;
  images?: string[] | null;
  features?: string[] | null;
  airbnb_url?: string | null;
  airbnb_embed_code?: string | null;
}

interface PropertyDetailDialogProps {
  property: Property | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PropertyDetailDialog({
  property,
  open,
  onOpenChange,
}: PropertyDetailDialogProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!property) return null;

  const images = property.images?.length
    ? property.images
    : [
        "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      ];

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const fullAddress = [
    property.address,
    property.city,
    property.state,
    property.zip_code,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0">
        {/* Image Gallery */}
        <div className="relative aspect-[16/9] bg-muted">
          <img
            src={images[currentImageIndex]}
            alt={`${property.title} - Image ${currentImageIndex + 1}`}
            className="w-full h-full object-cover"
          />
          {images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-background/80 backdrop-blur-sm rounded-full p-2 hover:bg-background transition-colors"
                aria-label="Previous image"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-background/80 backdrop-blur-sm rounded-full p-2 hover:bg-background transition-colors"
                aria-label="Next image"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
              {/* Dot indicators */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {images.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      idx === currentImageIndex
                        ? "bg-primary"
                        : "bg-background/60"
                    }`}
                    aria-label={`Go to image ${idx + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="p-6">
          <DialogHeader className="mb-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <Badge className="mb-2 bg-primary text-primary-foreground">
                  {propertyTypeLabels[
                    property.property_type as keyof typeof propertyTypeLabels
                  ] || property.property_type}
                </Badge>
                <DialogTitle className="font-display text-2xl font-bold text-foreground">
                  {property.title}
                </DialogTitle>
              </div>
              <SaveButton propertyId={property.id} />
            </div>
          </DialogHeader>

          {/* Location */}
          {(property.city || property.state) && (
            <div className="flex items-center gap-2 text-muted-foreground mb-4">
              <MapPin className="h-4 w-4 flex-shrink-0" />
              <span>{fullAddress || `${property.city}, ${property.state}`}</span>
            </div>
          )}

          {/* Stats */}
          <div className="flex flex-wrap items-center gap-6 text-muted-foreground mb-6">
            {property.bedrooms && (
              <div className="flex items-center gap-2">
                <Bed className="h-5 w-5" />
                <span>
                  {property.bedrooms} {property.bedrooms === 1 ? "bed" : "beds"}
                </span>
              </div>
            )}
            {property.bathrooms && (
              <div className="flex items-center gap-2">
                <Bath className="h-5 w-5" />
                <span>
                  {property.bathrooms}{" "}
                  {property.bathrooms === 1 ? "bath" : "baths"}
                </span>
              </div>
            )}
            {property.square_feet && (
              <div className="flex items-center gap-2">
                <Square className="h-5 w-5" />
                <span>{property.square_feet.toLocaleString()} sqft</span>
              </div>
            )}
          </div>

          {/* Description */}
          {property.description && (
            <div className="mb-6">
              <h3 className="font-semibold text-foreground mb-2">Description</h3>
              <p className="text-muted-foreground leading-relaxed">
                {property.description}
              </p>
            </div>
          )}

          {/* Features */}
          {property.features && property.features.length > 0 && (
            <div className="mb-6">
              <h3 className="font-semibold text-foreground mb-2">Features</h3>
              <div className="flex flex-wrap gap-2">
                {property.features.map((feature, i) => (
                  <Badge key={i} variant="secondary">
                    {feature}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Airbnb Embed */}
          {property.airbnb_embed_code && (
            <div className="mb-6">
              <h3 className="font-semibold text-foreground mb-2">
                Book on Airbnb
              </h3>
              <div
                className="rounded-lg overflow-hidden border border-border"
                dangerouslySetInnerHTML={{ __html: property.airbnb_embed_code }}
              />
            </div>
          )}

          {/* Pricing and Actions */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-border">
            <div className="font-display text-2xl font-bold text-primary">
              ${property.price?.toLocaleString()}
              <span className="text-base font-normal text-muted-foreground">
                /{property.price_period}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {property.property_type === "short-term" && property.airbnb_url && (
                <Button variant="outline" asChild>
                  <a
                    href={property.airbnb_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Book on Airbnb
                  </a>
                </Button>
              )}
              <Button asChild>
                <Link
                  to={`/contact?property=${property.id}`}
                  onClick={() => onOpenChange(false)}
                >
                  Inquire About This Property
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
